/**
 * Terminal knowledge — DB entries (admin-editable) + built-in fallbacks (airports, etc.).
 */

const CREATOR_LINES = [
  'I was built by Madushka Harshana Athapaththu for mySky Travels.',
  'I help you capture inquiries, search leads, and move stages — type help for commands.',
];

const AIRPORTS = {
  CMB: { name: 'Bandaranaike Intl', city: 'Colombo', country: 'Sri Lanka' },
  RML: { name: 'Ratmalana', city: 'Colombo', country: 'Sri Lanka' },
  DXB: { name: 'Dubai Intl', city: 'Dubai', country: 'UAE' },
  DWC: { name: 'Al Maktoum', city: 'Dubai', country: 'UAE' },
  AUH: { name: 'Abu Dhabi Intl', city: 'Abu Dhabi', country: 'UAE' },
  DOH: { name: 'Hamad Intl', city: 'Doha', country: 'Qatar' },
  KUL: { name: 'KL Intl', city: 'Kuala Lumpur', country: 'Malaysia' },
  SIN: { name: 'Changi', city: 'Singapore', country: 'Singapore' },
  BKK: { name: 'Suvarnabhumi', city: 'Bangkok', country: 'Thailand' },
  DMK: { name: 'Don Mueang', city: 'Bangkok', country: 'Thailand' },
  DEL: { name: 'Indira Gandhi Intl', city: 'Delhi', country: 'India' },
  BOM: { name: 'Chhatrapati Shivaji Intl', city: 'Mumbai', country: 'India' },
  MAA: { name: 'Chennai Intl', city: 'Chennai', country: 'India' },
  TRV: { name: 'Trivandrum Intl', city: 'Thiruvananthapuram', country: 'India' },
  MLE: { name: 'Velana Intl', city: 'Malé', country: 'Maldives' },
  HKG: { name: 'Hong Kong Intl', city: 'Hong Kong', country: 'China' },
  NRT: { name: 'Narita', city: 'Tokyo', country: 'Japan' },
  HND: { name: 'Haneda', city: 'Tokyo', country: 'Japan' },
  LHR: { name: 'Heathrow', city: 'London', country: 'UK' },
  CDG: { name: 'Charles de Gaulle', city: 'Paris', country: 'France' },
  FRA: { name: 'Frankfurt', city: 'Frankfurt', country: 'Germany' },
  JFK: { name: 'JFK', city: 'New York', country: 'USA' },
  SYD: { name: 'Kingsford Smith', city: 'Sydney', country: 'Australia' },
  MEL: { name: 'Melbourne', city: 'Melbourne', country: 'Australia' },
};

/** @type {object[]} */
let dbEntries = [];

export function setKnowledgeEntries(entries) {
  dbEntries = Array.isArray(entries) ? entries : [];
}

export async function fetchKnowledgeEntries(api) {
  const res = await api('/api/assistant/knowledge');
  setKnowledgeEntries(res.entries || []);
  return dbEntries;
}

function norm(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function looksLikeQuestion(s) {
  if (s.includes('?')) return true;
  return /^(who|what|where|when|why|how|tell me|explain|requirements? for|do i need)\b/.test(s);
}

function triggerMatches(trigger, normalizedQuery) {
  const t = norm(trigger);
  if (!t) return false;
  if (t.startsWith('/') && t.endsWith('/') && t.length > 2) {
    try {
      return new RegExp(t.slice(1, -1), 'i').test(normalizedQuery);
    } catch {
      return false;
    }
  }
  return normalizedQuery.includes(t);
}

function matchDbEntry(normalizedQuery) {
  const sorted = [...dbEntries].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  for (const entry of sorted) {
    if (entry.active === false) continue;
    const triggers = entry.triggers || [];
    if (triggers.some((t) => triggerMatches(t, normalizedQuery))) {
      return entry;
    }
  }
  return null;
}

function answerLines(entry) {
  return String(entry.answer || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

function matchCreator(s) {
  return /who (made|built|created|programmed) you|who are you|what are you|your creator|who is your (developer|maker)/.test(s);
}

function matchAirportQuery(s) {
  const codeHit = s.match(/\b([a-z]{3})\b/i);
  if (codeHit && AIRPORTS[codeHit[1].toUpperCase()]) {
    return codeHit[1].toUpperCase();
  }
  if (!/(airport|iata|code|what is|what's)\b/.test(s) && !/\b(cmb|dxb|kul|sin|bkk)\b/i.test(s)) {
    return null;
  }
  for (const [code, info] of Object.entries(AIRPORTS)) {
    const city = info.city.toLowerCase();
    if (s.includes(city) || s.includes(code.toLowerCase())) return code;
  }
  return null;
}

function fmtAmt(n) {
  if (n == null || !Number.isFinite(n)) return null;
  return `LKR ${Math.round(n).toLocaleString('en-LK')}`;
}

/** Append mySky visa cost when answer mentions a package name. */
function enrichWithVisaCost(lines, ctx) {
  if (!ctx.visaCosts || !lines.length) return lines;
  const text = lines.join(' ').toLowerCase();
  for (const [pkg, cost] of Object.entries(ctx.visaCosts)) {
    if (text.includes(pkg.toLowerCase())) {
      const price = fmtAmt(cost);
      if (price && !text.includes('lkr')) {
        return [...lines, `mySky reference cost: ${price} (confirm current rate with admin).`];
      }
    }
  }
  return lines;
}

/**
 * @param {string} raw
 * @param {{ visaCosts?: Record<string, number> }} ctx
 * @returns {string[]|null}
 */
export function tryKnowledgeAnswer(raw, ctx = {}) {
  const s = norm(raw);
  if (!s || s.length < 3) return null;

  const dbHit = matchDbEntry(s);
  if (dbHit) {
    const lines = enrichWithVisaCost(answerLines(dbHit), ctx);
    return lines.length ? lines : null;
  }

  if (!looksLikeQuestion(s) && !matchCreator(s) && !matchAirportQuery(s)) {
    return null;
  }

  if (matchCreator(s)) return CREATOR_LINES;

  if (/^(hi|hello|hey|good morning|good afternoon|good evening)\b/.test(s)) {
    return ['Hello — type new ticket, search, or help to get started.'];
  }

  const code = matchAirportQuery(s);
  if (code) {
    const a = AIRPORTS[code];
    return [`${code} — ${a.name}, ${a.city}, ${a.country}.`];
  }

  return null;
}
